import { useParams } from 'react-router-dom';
import { useGetEventDetail, useGetExperienceDetail } from '@/utils/detail';
import EventSidebarRS from '@/components/sections/event-details-page/EventSidebarRS';
import Seo from '@/components/headers/seo';
import Error404 from '@/components/error/404';
import FullpageAnimatedLogo from '@/components/ui/Loading/FullpageAnimatedLogo';
import RecommendedCarousels from '@/components/events/RecommendedCarousels';
import FeaturedFullScreen from '@/components/events/FeaturedFullScreen';
import EventDetailsRS from '@/components/sections/event-details-page/EventDetailsRS';
import EventHeroRS from '@/components/sections/event-details-page/HeroRS';

interface Props {
  pagetype: string;
}

const EventDetailAndExperiencesPage = ({ pagetype = 'events' }: Props) => {
  const { lang, slug } = useParams<{ lang: Language; slug: string }>();

  const isExperience = pagetype === 'experiences';

  // Call both hooks unconditionally, but disable the one we don't need
  const { data: eventData, isLoading: eventLoading } = useGetEventDetail(
    {
      lang,
      limit: 1,
      where: { slug },
    },
    {
      enabled: !isExperience,
    }
  );

  const { data: experienceData, isLoading: experienceLoading } = useGetExperienceDetail(
    {
      lang,
      limit: 1,
      where: { slug },
    },
    {
      enabled: isExperience,
    }
  );

  // Use the correct data based on pagetype
  const data = isExperience ? experienceData?.data : eventData?.data;
  const isLoading = isExperience ? experienceLoading : eventLoading;

  if (isLoading) {
    return <FullpageAnimatedLogo />;
  }

  if (!data) {
    return <Error404 />;
  }

  return (
    <>
      <Seo
        title={data.seo?.title || data.title}
        description={data.seo?.description}
        image={data.seo?.image?.url || data.image11?.url}
      />

      <EventHeroRS event={data} />
      <section
        id='rs-event-details'
        className='container flex flex-col-reverse gap-6 px-8 lg:flex-row lg:px-12 2xl:px-20'
      >
        <div className='grow overflow-hidden md:space-y-12'>
          <EventDetailsRS event={data} />
        </div>
        <aside className='mx-auto w-full shrink-0 grow space-y-6 lg:max-w-md'>
          <EventSidebarRS event={data} />
        </aside>
      </section>
      <RecommendedCarousels event={data} />
      <FeaturedFullScreen event={data} />
    </>
  );
};

export default EventDetailAndExperiencesPage;
